import { computed } from 'vue'
import { DateTime } from 'ts-luxon'
import type { ObjectTreeItemType } from '@/entities/ObjectsTree'
import { useTranslate } from '@/shared/lib'
import { forEach, has, isEmpty, isEqual, isObject } from 'lodash'

export const useHelpers = () => {
  /**
   * Вычисляем список часовых поясов
   */
  const timeZones = computed(() => {
    const zones: { title: string; value: string }[] = []

    for (let i = -12; i <= 12; i++) {
      const zone = (i > 0 ? '+' + i : i).toString()

      zones.push({
        value: zone,
        title: zone,
      })
    }

    return zones
  })

  /**
   * Функция генерации случайного ключа
   * @param length
   */
  const generateKey = (length: number = 32) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length)
      result += characters[randomIndex]
    }
    return result
  }

  /**
   * Преобразование даты из бд в необходимый формат
   * @param dateTime
   * @param format
   */
  const toTimeFormat = (dateTime: string, format: string = 'dd.MM.yyyy') => {
    return toTime(dateTime).toFormat(format)
  }

  /**
   * Преобразуем строку в DateTime
   * @param dateTime
   */
  const toTime = (dateTime: string) => {
    return DateTime.fromISO(dateTime.replace(' ', 'T').replace('Z', ''))
  }

  /**
   * Функция удаления элемента по его ид из дерева
   * @param data
   * @param id
   */
  const removeTreeElementById = (data: any, id: number) => {
    const index = data.findIndex((item) => item.id === id)

    if (index !== -1) {
      data.splice(index, 1)
      return true
    }

    for (let item of data) {
      if (item.children) {
        const result = removeTreeElementById(item.children, id)
        if (result) {
          return true
        }
      }
    }

    return false
  }

  /**
   * Функция поиска элемента по его ид в дереве
   * @param id
   * @param items
   */
  const findObjectToId = (id: number, items: any[]): any | undefined => {
    for (const item of items) {
      if (item.id === id) return item

      if (item.children.length) {
        const foundItem = findObjectToId(id, item.children)
        if (foundItem) return foundItem
      }
    }
  }

  /**
   * Функция подсчета типов объектов в категории
   * @param objects
   * @param counter
   */
  const updateCounter = (objects: any[], counter = {}) => {
    let items: any = { ...counter }

    objects.forEach((item: ObjectTreeItemType) => {
      if (items[item.type]) {
        items[item.type] = Number(items[item.type]) + 1
      } else {
        items[item.type] = 1
      }
    })

    return items
  }

  /**
   * Возвращает список цифр от нуля до to
   * @param to
   */
  const numberList = (to: number = 10) => {
    return Array.from({ length: to + 1 }, (_, index) => index)
  }

  /**
   * Функция обрезания списка родителей по id одного из родителей
   * @param parents
   * @param filterId
   */
  const filterParents = (parents: any, filterId: number) => {
    return parents.filter((_, index: number) => {
      const targetIndex = parents.findIndex((p: any) => p.id === filterId)
      return targetIndex !== -1 && index > targetIndex
    })
  }

  /**
   * Список месяцев
   */
  const monthsList = (decl: boolean = false) => {
    const { trs } = useTranslate()

    const field = decl ? 'months_gen' : 'months'

    return Array.from({ length: 12 }).map((_, key) => {
      return { value: key + 1, title: trs(`itf.${field}.${key + 1}`) }
    })
  }

  /**
   * Список дней недели
   */
  const weekdayList = () => {
    const { trs } = useTranslate()
    const week = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

    return week.map((item, key) => {
      return { value: key + 1, title: trs(`itf.weekdays.${item}`) }
    })
  }

  /**
   * Преобразовываем обычных показаний в кило
   * @param value
   */
  const numToKilo = (value: number | string) => {
    if (typeof value !== 'number') return value

    return (value / 1000).toFixed(2)
  }

  /**
   * Добавляет прозрачность к цвету в формате oklch
   * @param {string} oklchColor
   * @param {number} alpha - прозрачность (0-1)
   * @returns {string}
   */
  const addColorAlpha = (oklchColor: string, alpha: number) => {
    const inner = oklchColor.replace(/^oklch\(|\)$/g, '')
    return `oklch(${inner} / ${alpha})`
  }

  /**
   * Функция глубокого сравнения объектов
   * @param obj_1
   * @param obj_2
   */
  const getDifferentObject = (obj_1: Object, obj_2: Object) => {
    const diff = {}

    forEach(obj_2, (value, key) => {
      if (!has(obj_1, key)) {
        diff[key] = value
      } else {
        const value1 = obj_1[key]
        if (isObject(value) && isObject(value1)) {
          const nestedDiff = getDifferentObject(value1, value)
          if (!isEmpty(nestedDiff)) {
            diff[key] = nestedDiff
          }
        } else if (!isEqual(value, value1)) {
          diff[key] = value
        }
      }
    })

    return diff
  }

  /**
   * Функция сохранения файла на компьютер по ссылке
   * @param url
   * @param fileName
   */
  const saveFile = (url: string, fileName: string = '') => {
    const a = document.createElement('a')
    a.href = url

    if (fileName) a.download = fileName

    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  /**
   * Функция отправки файла на печать через скрытый iframe
   * @param file
   * @param type
   */
  const printFile = (file: BlobPart | Blob, type: string = 'application/pdf') => {
    const blob = file instanceof Blob ? file : new Blob([file], { type })
    const url = URL.createObjectURL(blob)

    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = url
    document.body.appendChild(iframe)

    iframe.onload = () => {
      try {
        const contentWindow = iframe.contentWindow

        // Чистим iframe только после реального завершения печати (печать/отмена),
        // иначе при переключении принтера/PDF диалог теряет источник и закрывается
        const cleanup = () => {
          if (!document.body.contains(iframe)) return
          document.body.removeChild(iframe)
          URL.revokeObjectURL(url)
        }

        contentWindow?.addEventListener('afterprint', cleanup)
        contentWindow?.print()
      } catch {
        window.open(url, '_blank')
        document.body.removeChild(iframe)
        URL.revokeObjectURL(url)
      }
    }
  }

  /**
   * Функция форматирования числа в строковое представление размера файла
   * @param bytes
   */
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Byte'

    const units = ['Byte', 'Kb', 'Mb', 'Gb', 'Tb']
    const k = 1024
    const dm = 2

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm))

    return `${size} ${units[i]}`
  }

  /**
   * Получаем цвет текста в зависимости от фона
   * @param hex
   */
  const getColorForBackground = (hex: string) => {
    const math = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

    const rgb = math
      ? {
          r: parseInt(math[1], 16),
          g: parseInt(math[2], 16),
          b: parseInt(math[3], 16),
        }
      : null

    const brightness = rgb ? (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 : 0

    return brightness > 128 ? '#000000' : '#FFFFFF'
  }

  /**
   * Определяет, является ли устройство мобильным
   * @returns {boolean} true если устройство мобильное
   */
  function isMobileDevice(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor

    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i

    if (mobileRegex.test(userAgent)) {
      return true
    }

    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.maxTouchPoints > 0 ||
      window.innerWidth <= 768 ||
      window.orientation !== undefined
    )
  }

  return {
    timeZones,
    generateKey,
    toTimeFormat,
    removeTreeElementById,
    updateCounter,
    numberList,
    filterParents,
    findObjectToId,
    monthsList,
    weekdayList,
    numToKilo,
    addColorAlpha,
    getDifferentObject,
    toTime,
    saveFile,
    printFile,
    formatFileSize,
    getColorForBackground,
    isMobileDevice,
  }
}
